import React, { useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Lock, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import { login, isAuthenticated } from '../../lib/admin-api';
import OMCMarkSquare from '../../components/branding/OMCMarkSquare';

export default function AdminLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const fromPath = location.state?.from || '/admin';
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // Already authenticated → redirect declaratively (no setState during render).
  if (isAuthenticated()) {
    return <Navigate to={fromPath} replace />;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!username || !password) {
      toast.error('Please enter your username and password');
      return;
    }
    setLoading(true);
    try {
      const result = await login(username, password);
      if (result?.success) {
        toast.success(`Welcome back, ${result.username}`);
        navigate(fromPath, { replace: true });
      }
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Login failed. Please try again.';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-coal text-bone flex items-center justify-center p-4" data-testid="admin-login-page">
      <div className="absolute inset-0 bg-grain opacity-[0.04] pointer-events-none" />
      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-block mb-4">
            <OMCMarkSquare size={64} className="mx-auto" />
          </div>
          <h1 className="editorial-headline text-3xl text-bone mb-2">OMC Admin Console</h1>
          <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-[hsl(var(--steel-blue-2))]">
            Ozark Mtn. Construction · Built for what matters
          </p>
        </div>
        <Card className="editorial-card-dark p-0">
          <div className="spec-strip" />
          <CardContent className="p-7 pt-9">
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="admin-login-form">
              <div>
                <Label htmlFor="admin-username" className="text-sm text-bone/85">Username</Label>
                <Input
                  id="admin-username"
                  type="text"
                  autoComplete="username"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  placeholder="laura"
                  className="mt-1.5 bg-coal/60 border-white/10 text-bone placeholder:text-bone/30"
                  data-testid="admin-login-username"
                  autoFocus
                />
              </div>
              <div>
                <Label htmlFor="admin-password" className="text-sm text-bone/85">Password</Label>
                <Input
                  id="admin-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="••••••••••••"
                  className="mt-1.5 bg-coal/60 border-white/10 text-bone placeholder:text-bone/30"
                  data-testid="admin-login-password"
                />
              </div>
              <Button
                type="submit"
                className="w-full btn-lift bg-primary hover:bg-[hsl(var(--hunter-2))] gap-2"
                disabled={loading}
                data-testid="admin-login-submit"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Signing in…
                  </span>
                ) : (
                  <>
                    <LogIn className="w-4 h-4" />
                    Sign in
                  </>
                )}
              </Button>
              <p className="flex items-start gap-2 text-[11px] text-bone/55 leading-relaxed pt-2 border-t border-white/8">
                <Lock className="w-3 h-3 mt-0.5 flex-shrink-0" />
                Authorized OMC personnel only. Session expires after 12 hours of inactivity.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
